import { useState } from 'react'
import { X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Select } from '@/components/ui/select'
import { useCreateUsageAdjustment } from '../queries/usageAdjustments'
import type { AdjustmentCategory, AdjustmentDirection } from '../types/usageAdjustment.types'

interface Props {
  accountId: string
  requestedBy: string
  onClose: () => void
}

// TODO: CAMPAIGN_EVENTS_HISTORICAL/WEBSITE_EVENTS_HISTORICAL/DUPLICATE_OFFSET and the three
// cross-cutting "typical reasons" were given directly; DATA_EXPORT_HISTORICAL/
// CAMPAIGN_SEND_HISTORICAL (outgress-only) are inferred placeholders, not confirmed.
const CROSS_CUTTING_OPTIONS: { value: AdjustmentCategory; label: string }[] = [
  { value: 'HISTORICAL_BACKFILL_PRE_ONBOARDING', label: 'Historical Backfill (Pre-Onboarding)' },
  { value: 'INCORRECT_EVENT_DATA', label: 'Incorrect Event Data Correction' },
  { value: 'REPROCESSING_DUPLICATE_OFFSET', label: 'Reprocessing Double-Count Offset' },
]

const CATEGORY_OPTIONS_BY_DIRECTION: Record<AdjustmentDirection, { value: AdjustmentCategory; label: string }[]> = {
  INGRESS: [
    { value: 'CAMPAIGN_EVENTS_HISTORICAL', label: 'Campaign Events Historical' },
    { value: 'WEBSITE_EVENTS_HISTORICAL', label: 'Website Events Historical' },
    { value: 'DUPLICATE_OFFSET', label: 'Duplicate Offset' },
    ...CROSS_CUTTING_OPTIONS,
    { value: 'OTHER', label: 'Other' },
  ],
  OUTGRESS: [
    { value: 'DATA_EXPORT_HISTORICAL', label: 'Data Export Historical' },
    { value: 'CAMPAIGN_SEND_HISTORICAL', label: 'Campaign Send Historical' },
    ...CROSS_CUTTING_OPTIONS,
    { value: 'OTHER', label: 'Other' },
  ],
}

const MAX_PERIOD_DAYS = 30

/** Confirmed 2026-07-15: max 30-day range, and it may not cross a calendar month boundary
 *  (e.g. 15 Apr–1 May is only 17 days but is NOT allowed — separate rule from the day count). */
function getMaxPeriodEnd(startStr: string): string | undefined {
  if (!startStr) return undefined
  const start = new Date(startStr + 'T00:00:00')
  const endOfMonth = new Date(start.getFullYear(), start.getMonth() + 1, 0)
  const plusMaxDays = new Date(start)
  plusMaxDays.setDate(plusMaxDays.getDate() + (MAX_PERIOD_DAYS - 1))
  const cap = endOfMonth < plusMaxDays ? endOfMonth : plusMaxDays
  return cap.toISOString().slice(0, 10)
}

function periodRangeError(start: string, end: string): string | null {
  if (!start || !end) return null
  if (end < start) return 'End date must be on or after the start date.'
  const startDate = new Date(start + 'T00:00:00')
  const endDate = new Date(end + 'T00:00:00')
  if (startDate.getFullYear() !== endDate.getFullYear() || startDate.getMonth() !== endDate.getMonth()) {
    return 'Period cannot cross a calendar month boundary.'
  }
  const days = Math.round((endDate.getTime() - startDate.getTime()) / 86_400_000) + 1
  if (days > MAX_PERIOD_DAYS) {
    return `Period cannot exceed ${MAX_PERIOD_DAYS} days (currently ${days}).`
  }
  return null
}

export function CreateAdjustmentModal({ accountId, requestedBy, onClose }: Props) {
  const [direction, setDirection] = useState<AdjustmentDirection>('INGRESS')
  const [category, setCategory] = useState<AdjustmentCategory | ''>('')
  const [targetPeriodStart, setTargetPeriodStart] = useState('')
  const [targetPeriodEnd, setTargetPeriodEnd] = useState('')
  const [eventCount, setEventCount] = useState('')
  const [reason, setReason] = useState('')
  const [ticketRef, setTicketRef] = useState('')

  const mutation = useCreateUsageAdjustment(accountId, requestedBy)

  const eventCountValue = Number(eventCount)
  const periodError = periodRangeError(targetPeriodStart, targetPeriodEnd)
  const periodRangeValid = !!targetPeriodStart && !!targetPeriodEnd && !periodError
  const isValid =
    !!category &&
    periodRangeValid &&
    eventCount.trim() !== '' &&
    !Number.isNaN(eventCountValue) &&
    eventCountValue !== 0 &&
    reason.trim().length > 0

  const handleDirectionChange = (d: AdjustmentDirection) => {
    setDirection(d)
    setCategory('') // category options are direction-scoped — reset on switch
  }

  const handlePeriodStartChange = (value: string) => {
    setTargetPeriodStart(value)
    const maxEnd = getMaxPeriodEnd(value)
    if (!targetPeriodEnd || targetPeriodEnd < value || (maxEnd && targetPeriodEnd > maxEnd)) {
      setTargetPeriodEnd(value)
    }
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!isValid || !category) return
    mutation.mutate(
      {
        direction,
        category,
        targetPeriodStart,
        targetPeriodEnd,
        eventCount: eventCountValue,
        reason: reason.trim(),
        ticketRef: ticketRef.trim() || undefined,
      },
      { onSuccess: onClose },
    )
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">New Usage Adjustment</h2>
            <p className="text-sm text-gray-500 mt-0.5">
              Submits for approval — no usage event is generated until approved.
            </p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          {/* Direction */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Direction <span className="text-red-500">*</span>
            </label>
            <div className="flex gap-4">
              {(['INGRESS', 'OUTGRESS'] as AdjustmentDirection[]).map((d) => (
                <label key={d} className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="direction"
                    value={d}
                    checked={direction === d}
                    onChange={() => handleDirectionChange(d)}
                    className="accent-primary"
                  />
                  <span className="text-sm text-gray-700">
                    {d === 'INGRESS' ? 'Ingress — data flowing in' : 'Outgress — data flowing out'}
                  </span>
                </label>
              ))}
            </div>
          </div>

          {/* Category */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Category <span className="text-red-500">*</span>
            </label>
            <Select
              value={category}
              onChange={(e) => setCategory(e.target.value as AdjustmentCategory)}
              options={CATEGORY_OPTIONS_BY_DIRECTION[direction]}
              placeholder="Select a category"
              required
            />
          </div>

          {/* Target period range */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Period Being Corrected <span className="text-red-500">*</span>
            </label>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Input
                  type="date"
                  value={targetPeriodStart}
                  onChange={(e) => handlePeriodStartChange(e.target.value)}
                  required
                  aria-label="Period start"
                />
                <p className="text-xs text-gray-400 mt-1">Start</p>
              </div>
              <div>
                <Input
                  type="date"
                  value={targetPeriodEnd}
                  min={targetPeriodStart || undefined}
                  max={getMaxPeriodEnd(targetPeriodStart)}
                  onChange={(e) => setTargetPeriodEnd(e.target.value)}
                  required
                  aria-label="Period end"
                />
                <p className="text-xs text-gray-400 mt-1">End (same as start for a single day)</p>
              </div>
            </div>
            <p className="text-xs text-gray-400 mt-1">
              Max {MAX_PERIOD_DAYS} days, within a single calendar month — one usage event is emitted per
              request, dated to the start of this period.
            </p>
            {periodError && (
              <p className="text-xs text-red-600 mt-1">{periodError}</p>
            )}
          </div>

          {/* Event count */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Event Count <span className="text-red-500">*</span>
            </label>
            <Input
              type="number"
              value={eventCount}
              onChange={(e) => setEventCount(e.target.value)}
              placeholder="e.g. 500 or -120"
              required
            />
            <p className="text-xs text-gray-400 mt-1">Positive to add, negative to subtract.</p>
          </div>

          {/* Ticket ref */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Ticket Reference (optional)
            </label>
            <Input
              value={ticketRef}
              onChange={(e) => setTicketRef(e.target.value)}
              placeholder="e.g. FHCORE-204"
              maxLength={50}
            />
          </div>

          {/* Reason */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Reason <span className="text-red-500">*</span>
            </label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Explain what happened and why this correction is needed — this is the only record of justification once submitted."
              maxLength={1000}
              rows={4}
              required
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring resize-none"
            />
          </div>

          {mutation.isError && (
            <p className="text-sm text-red-600 bg-red-50 rounded px-3 py-2">
              {(mutation.error as Error).message}
            </p>
          )}

          <div className="flex justify-end gap-3 pt-2">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={!isValid || mutation.isPending}>
              {mutation.isPending ? 'Submitting…' : 'Submit for Approval'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}
